/* eslint-disable max-lines-per-function */
import type { MouseEvent } from 'react';

import { CircleX, FlaskConical, Package, Wrench } from 'lucide-react';

import { formatDistance } from 'date-fns';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';

import {
  ContextSelectionType,
  useContextSelection,
} from '@/components/chat/ContextSelectionContext';

import { ContextSelectionItem } from './ContextSelectionContext';
import { iconColorClassMap } from './iconColorClassMap';

export const ContextSelectionCard = ({
  id,
  type,
  title,
  selectedVersions,
  lastEditLabel,
  onClick,
}: ContextSelectionItem & {
  onClick?: () => void;
}) => {
  const { removeContextSelection } = useContextSelection();

  const iconByType = {
    [ContextSelectionType.Experiment]: FlaskConical,
    [ContextSelectionType.Product]: Package,
    [ContextSelectionType.Tool]: Wrench,
  };

  const Icon = iconByType[type];
  const iconColorClass = iconColorClassMap[type];

  const handleDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    removeContextSelection({ id, type });
  };

  const numberOfVersions = selectedVersions?.length ?? 0;

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      className={cn(
        'border border-border-subtle rounded-md p-2 w-[208px] min-w-0 h-[96px] flex-none flex flex-col gap-1 bg-color-transparent items-start overflow-hidden',
        {
          'cursor-pointer': onClick,
        }
      )}
      onClick={onClick}
      {...(onClick && { role: 'button', tabIndex: 0 })}
    >
      <div className="flex justify-between h-4 w-full min-w-0 shrink-0">
        <Icon
          className={cn('h-4 w-4 shrink-0', iconColorClass)}
          data-testid={iconColorClass}
        />
        <Button variant="ghost" size="icon" onClick={handleDelete} asChild>
          <CircleX className="h-4 w-4 text-destructive cursor-pointer" />
        </Button>
      </div>
      <div className="flex min-w-0 w-full items-center">
        <p
          className="w-full min-w-0 text-sm font-semibold text-foreground truncate text-left"
          data-testid="context-selection-card-title"
        >
          {title}
        </p>
      </div>
      <div className="flex flex-col">
        <p className="text-sm text-muted-foreground leading-5 h-[20px] text-left">
          {numberOfVersions > 0 && (
            <>
              {numberOfVersions} version
              {numberOfVersions === 1 ? '' : 's'}
            </>
          )}
        </p>
        <p className="text-sm text-muted-foreground leading-5">
          {lastEditLabel &&
            formatDistance(new Date(lastEditLabel), new Date(), {
              addSuffix: true,
            })}
        </p>
      </div>
    </Component>
  );
};
